<template>
  <div class="relative w-full">
    <Select
      ref="select"
      :model-value="modelValue"
      :options="selectOptions"
      :disabled="disabled"
      :autofocus="autofocus"
      :on-focus="onFocus"
      :on-blur="onBlur"
      @update:model-value="updateValue"
    >
      <template #selected="{ option }">
        <div class="mr-1 inline-grid size-5 place-items-center align-top">
          <div
            class="size-4 rounded-full border border-border"
            :style="{ 'background-color': option?.id }"
          ></div>
        </div>

        {{ " " }}

        <template v-if="option">
          {{ option.label }}
        </template>
        <template v-else>
          <span class="text-muted-foreground">Select a color</span>
        </template>
      </template>

      <template #option="{ option }">
        <div class="mr-1 inline-grid size-5 place-items-center align-top">
          <EllipsisHorizontalIcon
            v-if="option.id === 'custom'"
            class="size-4 text-muted-foreground"
          />
          <div
            v-else
            class="size-4 rounded-full border border-border"
            :style="{ 'background-color': option.id }"
          ></div>
        </div>

        {{ option.label }}
      </template>
    </Select>

    <input
      ref="nativeInput"
      :value="modelValue"
      type="color"
      tabindex="-1"
      class="pointer-events-none absolute inset-0 size-full opacity-0"
      @input="onNativePickerInput"
    />
  </div>
</template>

<script lang="ts">
interface ColorPreset {
  label: string;
  color: string;
}

const defaultColorPresets: ColorPreset[] = [
  { label: "Buttondown blue", color: "#0069FF" },
  { label: "Blue", color: "#3B82F6" },
  { label: "Green", color: "#22C55E" },
  { label: "Yellow", color: "#F59E0B" },
  { label: "Red", color: "#EF4444" },
  { label: "Purple", color: "#8B5CF6" },
  { label: "Pink", color: "#EC4899" },
  { label: "Gray", color: "#6B7280" },
];
</script>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from "vue";

import EllipsisHorizontalIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";

import Select, { type SelectOption } from "./Select.vue";

const modelValue = defineModel<string>({ default: "" });

const nativeInput = useTemplateRef("nativeInput");

const props = withDefaults(
  defineProps<{
    presets?: ColorPreset[];
    required?: boolean;
    disabled?: boolean;
    autofocus?: boolean;
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (event: FocusEvent) => void;
  }>(),
  {
    presets: () => defaultColorPresets,
  }
);

const lastPicked = ref<string>();
watch(
  () => modelValue.value,
  (value) => {
    if (value && !props.presets.some((item) => item.color === value)) {
      lastPicked.value = value;
    }
  },
  { immediate: true }
);

const selectOptions = computed((): SelectOption[] => {
  const options: SelectOption[] = [];

  if (!props.required) {
    options.push({
      id: "",
      label: `None`,
    });
  }

  for (const preset of props.presets) {
    options.push({
      id: preset.color,
      label: preset.label,
    });
  }

  if (lastPicked.value) {
    options.push({
      id: lastPicked.value,
      label: `Most recently used (${lastPicked.value})`,
    });
  }

  options.push({
    id: "custom",
    label: "Custom color",
  });

  return options;
});

const updateValue = (next: string) => {
  if (next === "custom") {
    nativeInput.value?.click();
    return;
  }

  modelValue.value = next;
};

const onNativePickerInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  modelValue.value = target.value;
};

const select = useTemplateRef("select");

defineExpose({
  input: computed(() => select.value?.input),
});
</script>
